import { supabase } from './supabase';
import { getApiUrl } from './apiConfig';
import { notConnectedBlockReason, type SocialPage } from './pageChoice';

export interface ScheduleTargets {
  /** Alleen nog "niet gekoppeld"-blokkades; doelkeuze blokkeert niet meer. */
  blockReason: string | null;
  pages: SocialPage[];
  /** Profiel-default uit social_profiles (facebook_page_id / linkedin_page_id). */
  defaultPageId: string | null;
  /** true bij LinkedIn: persoonlijk profiel is altijd een geldige optie. */
  personalOption: boolean;
}

export const NO_TARGETS: ScheduleTargets = {
  blockReason: null,
  pages: [],
  defaultPageId: null,
  personalOption: false,
};

/**
 * Doelen voor de inplan-dialoog: koppelingsstatus uit social_profiles +
 * paginalijst en huidige default via de facebook-pages edge function.
 * Fail-open bij API-problemen (lege lijst, geen blokkade): de
 * delivery-laag blijft het echte vangnet.
 */
export async function loadScheduleTargets(
  platform: string,
): Promise<ScheduleTargets> {
  if (platform !== 'facebook' && platform !== 'linkedin') return NO_TARGETS;
  const personalOption = platform === 'linkedin';

  try {
    const { data: authData } = await supabase.auth.getUser();
    if (!authData?.user) return { ...NO_TARGETS, personalOption };

    const { data: dbUser } = await supabase
      .from('users')
      .select('id')
      .eq('auth_user_id', authData.user.id)
      .maybeSingle();
    if (!dbUser) return { ...NO_TARGETS, personalOption };

    const { data: profile } = await supabase
      .from('social_profiles')
      .select('connected_platforms')
      .eq('user_id', dbUser.id)
      .maybeSingle();
    const acct = (
      (profile?.connected_platforms ?? {}) as Record<
        string,
        { handle?: string; reauth_required?: boolean } | '' | undefined
      >
    )[platform];
    const connected = !!(
      acct &&
      typeof acct === 'object' &&
      acct.handle &&
      !acct.reauth_required
    );
    if (!connected) {
      return {
        ...NO_TARGETS,
        personalOption,
        blockReason: notConnectedBlockReason(platform, false),
      };
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) return { ...NO_TARGETS, personalOption };

    const res = await fetch(getApiUrl('/functions/v1/facebook-pages'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ action: 'list', platform }),
    });
    if (!res.ok) return { ...NO_TARGETS, personalOption };
    const body = await res.json();
    if (!body?.success) return { ...NO_TARGETS, personalOption };

    return {
      blockReason: null,
      pages: (body.pages ?? []) as SocialPage[],
      defaultPageId: (body.current_page_id as string | null) ?? null,
      personalOption,
    };
  } catch {
    return { ...NO_TARGETS, personalOption };
  }
}
